import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Address } from 'viem';
import { normalizeAddress } from './address';
import { WalletContext, type WalletContextValue } from './context';
import { createWalletDiscovery, type WalletDiscovery } from './discovery';
import { isRequestPending, isUserRejection, walletErrorCodes, walletErrorMessage } from './errors';
import { clearLastWallet, loadLastWallet, saveLastWallet } from './storage';
import { isSwitchingChain, SWITCH_GRACE_MS } from './switchGuard';
import type { EIP6963ProviderDetail, WalletStatus } from './types';
import { t } from '../i18n/i18n';

interface State {
  status: WalletStatus;
  address: Address | null;
  chainId: number | null;
  active: EIP6963ProviderDetail | null;
  error: string | null;
}

const INITIAL: State = { status: 'disconnected', address: null, chainId: null, active: null, error: null };

function parseChainId(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v !== 'string') return null;
  const n = v.startsWith('0x') || v.startsWith('0X') ? parseInt(v, 16) : parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}

function firstAccount(v: unknown): Address | null {
  return Array.isArray(v) ? normalizeAddress(v[0]) : null;
}

function errorMessage(e: unknown): string {
  if (isUserRejection(e)) return t('wallet.err.rejected');
  if (isRequestPending(e)) return t('wallet.err.pending');
  const msg = walletErrorMessage(e);
  if (msg && msg !== '[object Object]') return msg;
  const code = walletErrorCodes(e)[0];
  return code !== undefined ? t('wallet.err.code', { code: String(code) }) : t('wallet.err.failed');
}

/** MetaMask has emitted disconnect(1013 "Try again later") while switching networks; it is not a real disconnect. */
const TRANSIENT_DISCONNECT = 1013;

async function readChainId(d: EIP6963ProviderDetail): Promise<number | null> {
  try {
    return parseChainId(await d.provider.request({ method: 'eth_chainId' }));
  } catch {
    return null;
  }
}

/**
 * Wallet connection state for the whole app. Discovers wallets (EIP-6963 + window.ethereum fallback),
 * connects with eth_requestAccounts, silently restores the last wallet with eth_accounts, and follows
 * accountsChanged / chainChanged / disconnect. It never switches chains.
 */
export function WalletProvider({ children, autoReconnect = true }: { children: ReactNode; autoReconnect?: boolean }) {
  const [state, setState] = useState<State>(INITIAL);
  const [wallets, setWallets] = useState<readonly EIP6963ProviderDetail[]>([]);
  const discoveryRef = useRef<WalletDiscovery | null>(null);
  const detachRef = useRef<(() => void) | null>(null);
  /** Incremented by every connect/disconnect; async results from an older op are dropped. */
  const opRef = useRef(0);
  const triedReconnectRef = useRef(!autoReconnect);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const d = createWalletDiscovery(window);
    discoveryRef.current = d;
    setWallets(d.getWallets());
    const unsubscribe = d.subscribe(() => setWallets(d.getWallets()));
    return () => {
      unsubscribe();
      d.destroy();
      discoveryRef.current = null;
    };
  }, []);

  // Detach provider listeners on unmount.
  useEffect(() => () => detachRef.current?.(), []);

  const attach = useCallback((d: EIP6963ProviderDetail) => {
    detachRef.current?.();
    let recheckTimer: ReturnType<typeof setTimeout> | null = null;
    let detached = false;
    const applyAccounts = (accounts: unknown, onEmpty: Partial<State>) => {
      const a = firstAccount(accounts);
      setState((s) => {
        if (s.active !== null && s.active !== d) return s; // stale listener of a previous wallet
        return a ? { ...s, status: 'connected', address: a, error: null } : { ...s, status: 'disconnected', address: null, ...onEmpty };
      });
    };
    /**
     * During our chain-1337 switch some wallets emit accountsChanged([]) or disconnect. Do not reset the UI on that:
     * ask eth_accounts again once the switch has settled and trust that answer.
     */
    const recheckLater = (onEmpty: Partial<State>) => {
      if (recheckTimer) clearTimeout(recheckTimer);
      recheckTimer = setTimeout(() => {
        recheckTimer = null;
        if (detached) return;
        if (isSwitchingChain()) {
          recheckLater(onEmpty);
          return;
        }
        d.provider.request({ method: 'eth_accounts' }).then(
          (accounts) => !detached && applyAccounts(accounts, onEmpty),
          () => !detached && applyAccounts([], onEmpty),
        );
      }, SWITCH_GRACE_MS);
    };
    const onAccounts = (accounts: unknown) => {
      // Empty list = wallet locked or site permission revoked. Stay subscribed so an unlock reconnects.
      if (!firstAccount(accounts) && isSwitchingChain()) {
        recheckLater({});
        return;
      }
      applyAccounts(accounts, {});
    };
    // chainChanged (including the one our own 1337 switch causes) only records the chain id: never a reset.
    const onChain = (chainId: unknown) => setState((s) => ({ ...s, chainId: parseChainId(chainId) }));
    const onDisconnect = (err?: unknown) => {
      const lost: Partial<State> = { error: t('wallet.err.disconnected') };
      if (isSwitchingChain() || walletErrorCodes(err).includes(TRANSIENT_DISCONNECT)) {
        recheckLater(lost);
        return;
      }
      setState((s) => ({ ...s, status: 'disconnected', address: null, ...lost }));
    };
    d.provider.on('accountsChanged', onAccounts);
    d.provider.on('chainChanged', onChain);
    d.provider.on('disconnect', onDisconnect);
    detachRef.current = () => {
      detached = true;
      if (recheckTimer) clearTimeout(recheckTimer);
      d.provider.removeListener('accountsChanged', onAccounts);
      d.provider.removeListener('chainChanged', onChain);
      d.provider.removeListener('disconnect', onDisconnect);
      detachRef.current = null;
    };
  }, []);

  const connect = useCallback(
    async (rdns?: string): Promise<Address | null> => {
      const list = discoveryRef.current?.getWallets() ?? wallets;
      const d = rdns ? list.find((w) => w.info.rdns === rdns) : list[0];
      if (!d) {
        setState((s) => ({ ...s, error: t('wallet.err.none') }));
        return null;
      }
      const op = ++opRef.current;
      triedReconnectRef.current = true;
      setState((s) => ({ ...s, status: 'connecting', error: null }));
      try {
        const address = firstAccount(await d.provider.request({ method: 'eth_requestAccounts' }));
        if (op !== opRef.current) return null;
        if (!address) throw new Error(t('wallet.err.no_account'));
        const chainId = await readChainId(d);
        if (op !== opRef.current) return null;
        attach(d);
        saveLastWallet(d.info.rdns);
        setState({ status: 'connected', address, chainId, active: d, error: null });
        return address;
      } catch (e) {
        if (op !== opRef.current) return null;
        detachRef.current?.();
        setState({ ...INITIAL, error: errorMessage(e) });
        return null;
      }
    },
    [attach, wallets],
  );

  const disconnect = useCallback(() => {
    opRef.current++;
    triedReconnectRef.current = true;
    const d = stateRef.current.active;
    detachRef.current?.();
    clearLastWallet();
    setState(INITIAL);
    if (d) {
      // Best-effort: MetaMask supports this (EIP-2255 extension); other wallets may throw. Never block on it.
      void d.provider.request({ method: 'wallet_revokePermissions', params: [{ eth_accounts: {} }] }).catch(() => {});
    }
  }, []);

  // Silent auto-reconnect: once the remembered wallet is discovered, ask eth_accounts (no prompt).
  useEffect(() => {
    if (triedReconnectRef.current) return;
    const rdns = loadLastWallet();
    if (!rdns) {
      triedReconnectRef.current = true;
      return;
    }
    const d = wallets.find((w) => w.info.rdns === rdns);
    if (!d) return; // not announced (yet)
    triedReconnectRef.current = true;
    const op = ++opRef.current;
    setState((s) => ({ ...s, status: 'reconnecting' }));
    void (async () => {
      try {
        const address = firstAccount(await d.provider.request({ method: 'eth_accounts' }));
        if (op !== opRef.current) return;
        if (!address) {
          setState(INITIAL);
          return;
        }
        const chainId = await readChainId(d);
        if (op !== opRef.current) return;
        attach(d);
        setState({ status: 'connected', address, chainId, active: d, error: null });
      } catch {
        if (op === opRef.current) setState(INITIAL);
      }
    })();
  }, [wallets, attach]);

  const refreshWallets = useCallback(() => discoveryRef.current?.refresh(), []);
  const clearError = useCallback(() => setState((s) => (s.error ? { ...s, error: null } : s)), []);

  const value = useMemo<WalletContextValue>(() => {
    const connected = state.status === 'connected';
    return {
      status: state.status,
      address: connected ? state.address : null,
      chainId: state.chainId,
      provider: connected && state.active ? state.active.provider : null,
      walletInfo: state.active?.info ?? null,
      wallets,
      error: state.error,
      connect,
      disconnect,
      refreshWallets,
      clearError,
    };
  }, [state, wallets, connect, disconnect, refreshWallets, clearError]);

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
